import React from 'react';
import { Box, Inline } from '@styleseat/ui';
import Stack from '../Stack';
import BodyText from '../BodyText';
import TextInput, { TextInputDefaultProps, TextInputProps } from './TextInput';

export type BaseTextAreaProps = TextInputProps & {
  /** Field label */
  label?: React.ReactNode;
  /** Color of borders and message text */
  accentColor?: string;
  /** Text or a component to display below the input component */
  message?: React.ReactNode;
  /**
   * - `bordered` (default) uses a bordered textarea
   * - `stretched` to indicate there should be no margin from container edges or border
   * - `stretched-aligned` is similar to stretched, except it also removes left and right padding
   *        in the textarea so that the placeholder is aligned with the label
   */
  appearance?: 'stretched' | 'bordered' | 'stretch-aligned';

  // These props are suggested by the braid design system
  // If you need to expand props to support one of these types of functionality,
  // please use one of the props below
  // /** A label which appears immediately to the right of the label */
  // secondaryLabel: nodeOrText,
  // /** Label which appears on the opposite side of the field from the label */
  // tertiaryLabel: nodeOrText,
  // /** Text which appears below label row, displayed in the accent color */
  // description: nodeOrText,
  // /** True to reserve space for the message below the input, otherwise false */
  // reserveMessageSpace: bool,
  // /** True when the field is required, otherwise false */
  // required: bool,
};

/**
 * Implements the API shared between `TextField` and `Textarea`.
 */
const BaseTextArea: React.FCWithChildren<BaseTextAreaProps> = React.forwardRef(
  ({
    message, label, appearance, accentColor, ...restProps
  }, ref) => {
    // figure out what to display as our label
    let labelComponent = null;
    if (label) {
      if (typeof label === 'string') {
        labelComponent = (
          <BodyText appearance="smaller" weight="bold">
            {label}
          </BodyText>
        );
      } else {
        labelComponent = label;
      }
    }

    // figure out what to display as our message
    let messageComponent = null;
    if (message) {
      if (typeof message === 'string') {
        messageComponent = (
          <Inline>
            <BodyText color={accentColor}>{message}</BodyText>
          </Inline>
        );
      } else {
        messageComponent = message;
      }
    }

    return (
      <Box padding={appearance === 'bordered' ? 16 : 0}>
        <Stack>
          {!!labelComponent && <Inline>{labelComponent}</Inline>}
          <TextInput ref={ref} appearance={appearance} {...restProps} />
          {messageComponent}
        </Stack>
      </Box>
    );
  },
);

BaseTextArea.defaultProps = {
  ...TextInputDefaultProps,
  label: null,
  message: null,
  appearance: 'bordered',
  type: 'text',
  multiline: true,
};

export default BaseTextArea;
